/**
 * InspectPage - Docker Container Inspection Page
 *
 * Displays real-time status of all docker containers
 * Shows container health, status, and basic information
 */

import { useState, useEffect } from 'react'
import api from '../services/api'
import { normalizeErrorMessage } from '../utils/dataValidator'

interface ContainerStatus {
  name: string
  service: string
  status: string
  health: string | null
  image: string
}

interface SystemStatus {
  docker_available: boolean
  containers: ContainerStatus[]
  total_containers: number
  running_containers: number
  healthy_containers: number
}

const InspectPage = () => {
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())

  const fetchSystemStatus = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/system/status')
      setSystemStatus(response.data)
      setLastUpdate(new Date())
    } catch (err) {
      setError(normalizeErrorMessage(err, 'Failed to fetch system status'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSystemStatus()

    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchSystemStatus, 10000)

    return () => clearInterval(interval)
  }, [])

  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase()
    if (statusLower.includes('running') || statusLower.includes('up')) {
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
    }
    if (statusLower.includes('exited') || statusLower.includes('down')) {
      return 'bg-red-500/15 text-red-300 border-red-500/40'
    }
    return 'bg-white/5 text-[var(--rq-text-soft)] border-white/10'
  }

  const getHealthIcon = (health: string | null): string => {
    if (health === 'healthy') return '✓'
    if (health === null) return '-'
    return '!'
  }

  const getHealthColor = (health: string | null): string => {
    if (health === 'healthy') return 'text-emerald-400'
    if (health === null) return 'text-[var(--rq-text-soft)]'
    return 'text-amber-300'
  }

  if (loading && !systemStatus) {
    return (
      <div className="rq-page">
        <div className="rq-card p-8 text-center text-[var(--rq-text-soft)]">Loading system status...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rq-page">
        <div className="rq-card p-4 border-red-700/60 bg-red-900/25">
          <h3 className="text-red-300 font-semibold mb-2">Error Loading System Status</h3>
          <p className="text-red-200">{error}</p>
          <button
            onClick={fetchSystemStatus}
            className="mt-4 rq-btn-primary px-4 py-2"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!systemStatus) {
    return null
  }

  return (
    <div className="rq-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="rq-title mb-2">System Inspection</h1>
        <p className="rq-subtitle">
          Docker container status and health monitoring
        </p>
        <div className="mt-2 text-sm text-[var(--rq-text-soft)]">
          Last updated: {lastUpdate.toLocaleTimeString()}
          <button
            onClick={fetchSystemStatus}
            className="ml-4 text-[var(--rq-blue)] hover:text-[var(--rq-magenta)] transition-colors"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="rq-card p-6">
          <div className="text-sm font-medium text-[var(--rq-text-soft)] mb-1">Docker Status</div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${systemStatus.docker_available ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
            <div className="text-2xl font-bold text-[var(--rq-text)]">
              {systemStatus.docker_available ? 'Available' : 'Unavailable'}
            </div>
          </div>
        </div>

        <div className="rq-card p-6">
          <div className="text-sm font-medium text-[var(--rq-text-soft)] mb-1">Total Containers</div>
          <div className="text-2xl font-bold text-[var(--rq-text)]">
            {systemStatus.total_containers}
          </div>
        </div>

        <div className="rq-card p-6">
          <div className="text-sm font-medium text-[var(--rq-text-soft)] mb-1">Running</div>
          <div className="text-2xl font-bold text-emerald-600">
            {systemStatus.running_containers}
          </div>
        </div>

        <div className="rq-card p-6">
          <div className="text-sm font-medium text-[var(--rq-text-soft)] mb-1">Healthy</div>
          <div className="text-2xl font-bold text-emerald-600">
            {systemStatus.healthy_containers}
          </div>
        </div>
      </div>

      {/* Container List */}
      <div className="rq-card overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h2 className="text-xl font-semibold text-[var(--rq-text)]">Container Status</h2>
        </div>

        {systemStatus.containers.length === 0 ? (
          <div className="p-8 text-center text-[var(--rq-text-soft)]">
            No containers found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-black/20">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--rq-text-soft)] uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--rq-text-soft)] uppercase tracking-wider">
                    Container Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--rq-text-soft)] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--rq-text-soft)] uppercase tracking-wider">
                    Health
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--rq-text-soft)] uppercase tracking-wider">
                    Image
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {systemStatus.containers.map((container, index) => (
                  <tr key={index} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-[var(--rq-text)]">
                        {container.service}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[var(--rq-text-soft)]">
                        {container.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusColor(container.status)}`}>
                        {container.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-2xl ${getHealthColor(container.health)}`}>
                        {getHealthIcon(container.health)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-[var(--rq-text-soft)] max-w-xs truncate">
                        {container.image}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Additional Info */}
      <div className="mt-6 text-sm text-[var(--rq-text-soft)]">
        <p>
          This page displays real-time information about Docker containers running in the system.
          The status refreshes automatically every 10 seconds.
        </p>
      </div>
    </div>
  )
}

export default InspectPage
